import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { APP_BASE } from "@/lib/paths";
import { sectionForPathname } from "@/lib/sections";
import { Milk } from "lucide-react";

const Loader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="flex h-12 w-12 animate-pulse items-center justify-center rounded-xl bg-[hsl(var(--brand))] text-white">
        <Milk className="h-6 w-6" />
      </div>
      <span className="text-sm text-muted-foreground">Loading LactoPilot…</span>
    </div>
  </div>
);

// Staff-only routes. Clients are redirected to their portal.
export const ProtectedRoute = ({ children }) => {
  const { user, ready, permsReady, canAccess } = useAuth();
  const location = useLocation();
  if (!ready || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "superadmin") return <Navigate to="/superadmin" replace />;
  if (user.role === "client") return <Navigate to="/portal" replace />;
  if (!permsReady) return <Loader />;

  const section = sectionForPathname(location.pathname);
  // Users & Permissions pages are always admin-only.
  if ((section === "users" || section === "permissions") && user.role !== "admin") {
    return <Navigate to={APP_BASE} replace />;
  }
  // Expenses is gated purely by role (admin/manager), independent of the section RBAC matrix.
  if (section === "expenses") {
    if (user.role !== "admin" && user.role !== "manager") return <Navigate to={APP_BASE} replace />;
    return children;
  }
  // Any other section must be granted to the user's role.
  if (section && !canAccess(section)) return <Navigate to={APP_BASE} replace />;
  return children;
};

// Client-portal-only routes. Staff are redirected to the main app.
export const ClientRoute = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "client") return <Navigate to={APP_BASE} replace />;
  return children;
};
